import { Link } from 'react-router-dom';
import { VestaLogo } from '@/components/VestaLogo';
import { cn } from '@/lib/utils';
import { VESTA_SALES_CALENDAR } from '@/brand/vesta-cfo-brand';
import { SCHEDULE_CALL_CTA } from '@/lib/marketing-cta';

export interface SiteFooterProps {
  /** Light (marketing / cream pages) or dark (landing, docs). */
  variant?: 'light' | 'dark';
  /** Pricing callout strip (defaults to true on light pages; pass explicitly on dark landing). */
  showPricingCallout?: boolean;
  className?: string;
}

const FOOTER_LINKS = {
  product: [
    { label: 'Features', to: '/features' },
    { label: 'Partners', to: '/partners' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Integrations', to: '/integrations' },
  ],
  resources: [
    { label: 'Support', to: '/support' },
    { label: 'Blog', to: '/blog' },
    { label: 'Press', to: '/press' },
  ],
  company: [
    { label: 'About Vesta', to: '/company' },
    { label: 'Team', to: '/about' },
    { label: 'Careers', to: '/careers' },
    { label: 'Contact', to: '/contact' },
  ],
  legal: [
    { label: 'Privacy', to: '/privacy' },
    { label: 'Terms', to: '/terms' },
    { label: 'Security', to: '/security' },
    { label: 'Trust & subprocessors', to: '/trust' },
    { label: 'System status', to: '/status' },
  ],
} as const;

export function SiteFooter({
  variant = 'light',
  showPricingCallout,
  className,
}: SiteFooterProps) {
  const isLight = variant === 'light';
  const showBanner = showPricingCallout ?? isLight;
  const year = new Date().getFullYear();

  const linkClass = isLight
    ? 'text-vesta-navy/75 hover:text-vesta-gold transition-colors'
    : 'text-white/55 hover:text-vesta-gold transition-colors';
  const headingClass = isLight
    ? 'text-sm font-semibold text-vesta-navy mb-3'
    : 'text-xs font-semibold uppercase tracking-widest text-white/90 mb-3';

  return (
    <footer
      className={cn(
        isLight
          ? 'border-t border-vesta-navy/10 bg-gradient-to-b from-vesta-mist/80 to-vesta-mist text-vesta-navy'
          : 'border-t border-white/8 bg-vesta-navy text-white',
        className,
      )}
    >
      <div className={cn('mx-auto max-w-6xl px-6 py-12 md:px-10 md:py-16', isLight && 'md:py-14')}>
        {showBanner && <PricingBanner isLight={isLight} />}

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          <div className="sm:col-span-2 lg:col-span-4">
            <VestaLogo size="sm" tone={isLight ? 'light' : 'dark'} />
            <p
              className={cn(
                'mt-4 max-w-sm text-sm leading-relaxed',
                isLight ? 'text-vesta-navy/75' : 'text-white/50',
              )}
            >
              <span className={cn('font-semibold', isLight ? 'text-vesta-navy' : 'text-white/90')}>
                Vesta CFO
              </span>{' '}
              — AI financial intelligence for independent hotels: KPIs, daily briefings, and alerts in one workspace.
            </p>
          </div>

          <FooterColumn title="Product" headingClass={headingClass} linkClass={linkClass}>
            {FOOTER_LINKS.product.map(({ label, to }) => (
              <li key={to}>
                <Link to={to}>{label}</Link>
              </li>
            ))}
            <li>
              <a href={VESTA_SALES_CALENDAR} target="_blank" rel="noopener noreferrer">
                {SCHEDULE_CALL_CTA}
              </a>
            </li>
          </FooterColumn>

          <FooterColumn title="Resources" headingClass={headingClass} linkClass={linkClass}>
            {FOOTER_LINKS.resources.map(({ label, to }) => (
              <li key={to}>
                <Link to={to}>{label}</Link>
              </li>
            ))}
          </FooterColumn>

          <FooterColumn title="Company" headingClass={headingClass} linkClass={linkClass}>
            {FOOTER_LINKS.company.map(({ label, to }) => (
              <li key={to}>
                <Link to={to}>{label}</Link>
              </li>
            ))}
          </FooterColumn>

          <FooterColumn title="Legal" headingClass={headingClass} linkClass={linkClass} className="sm:col-span-2 lg:col-span-2">
            {FOOTER_LINKS.legal.map(({ label, to }) => (
              <li key={to}>
                <Link to={to}>{label}</Link>
              </li>
            ))}
          </FooterColumn>
        </div>

        <CopyrightBar isLight={isLight} year={year} />
      </div>
    </footer>
  );
}

function PricingBanner({ isLight }: { isLight: boolean }) {
  return (
    <div
      className={cn(
        'mb-12 flex flex-col gap-4 rounded-xl border px-5 py-4 sm:flex-row sm:items-center sm:justify-between',
        isLight
          ? 'border-vesta-navy/10 bg-white/60 shadow-sm'
          : 'border-white/10 bg-white/[0.04]',
      )}
    >
      <div>
        <p className={cn('text-sm font-medium', isLight ? 'text-vesta-navy' : 'text-white/90')}>
          {isLight
            ? 'Plans for independents, groups, and management companies'
            : 'From $299/mo · Starter, Growth, Enterprise'}
        </p>
        <p className={cn('mt-0.5 text-xs', isLight ? 'text-vesta-navy/60' : 'text-white/45')}>
          {isLight
            ? 'Compare features on the pricing page — multi-property packages are scoped on a short call.'
            : 'Full tier breakdown, FAQ, and what\u2019s included.'}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-4">
        <Link
          to="/pricing"
          className={cn(
            'text-sm font-semibold transition-colors',
            isLight ? 'text-vesta-gold hover:text-vesta-navy' : 'text-vesta-gold hover:text-white',
          )}
        >
          View plans →
        </Link>
        <a
          href={VESTA_SALES_CALENDAR}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'text-sm font-semibold transition-colors',
            isLight ? 'text-vesta-navy/70 hover:text-vesta-navy' : 'text-white/55 hover:text-white',
          )}
        >
          {SCHEDULE_CALL_CTA} →
        </a>
      </div>
    </div>
  );
}

function FooterColumn({
  title,
  headingClass,
  linkClass,
  className,
  children,
}: {
  title: string;
  headingClass: string;
  linkClass: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('lg:col-span-2', className)}>
      <h3 className={headingClass}>{title}</h3>
      <ul className={cn('space-y-2.5 text-sm', linkClass)}>{children}</ul>
    </div>
  );
}

function CopyrightBar({
  isLight,
  year,
}: {
  isLight: boolean;
  year: number;
}) {
  return (
    <div
      className={cn(
        'mt-12 border-t pt-6 text-xs',
        isLight ? 'border-vesta-navy/10 text-vesta-navy/55' : 'border-white/8 text-white/35',
      )}
    >
      © {year} Vesta · Vesta CFO
    </div>
  );
}
